import { useEffect } from "react";
import { useCourseDetailStore } from "@/stores/courseDetailStore";
import { reportLearningProgress } from "@/lib/reportLearningProgress";
import type { ValidationSubmission } from "@/lib/validationSubmission";
import {
  getInteractiveElementState,
  setInteractiveElementState,
  InteractiveElementStatusKind,
} from "@/lib/interactiveElementState";

/**
 * Reports progress for the server-validated code quizzes (CODE_ASSERTION,
 * CODE_DEBUG, CODE_EXPLANATION, CODE_FREETEXT) whose durable state lives in
 * `validation_data`.
 *
 * Status is derived from how many of the quiz's `total` entries have a saved
 * submission and written to BOTH layers — the local interactive state (TOC
 * badge) and the server `LearningProgress` record — idempotently:
 *
 *   no submissions          → nothing reported (not started)
 *   some but not all entries → in progress
 *   every entry submitted    → completed (regardless of correctness)
 *
 * Attempt-capped types hand in `finished` instead, since a submitted entry
 * that still has attempts left is not done yet.
 *
 * The write is guarded against the current local state so re-renders and
 * cross-device re-hydration from saved submissions don't re-post or inflate the
 * attempt counter, and a completed element is never downgraded.
 */
export const useCodeQuizProgress = (
  id: string,
  submissions: Record<number, ValidationSubmission>,
  total: number,
  finished?: boolean
): void => {
  const { enrollmentId, trainerMode, refreshProgress } = useCourseDetailStore();
  const attempted = Object.keys(submissions).length;
  const correct = Object.values(submissions).filter(
    (s) => s.correct === true
  ).length;

  useEffect(() => {
    if (!enrollmentId || trainerMode || total <= 0 || attempted <= 0) return;

    const completed = finished ?? attempted >= total;
    const targetKind = completed
      ? InteractiveElementStatusKind.Completed
      : InteractiveElementStatusKind.InProgress;

    const current = getInteractiveElementState(id, enrollmentId);
    if (current.kind === targetKind) return;
    // Never downgrade an already-completed element back to in-progress.
    if (current.kind === InteractiveElementStatusKind.Completed) return;

    if (completed) {
      setInteractiveElementState(id, enrollmentId, {
        kind: InteractiveElementStatusKind.Completed,
        score: { correct, total },
        attempts: 1,
        completedAt: new Date().toISOString(),
      });
    } else {
      setInteractiveElementState(id, enrollmentId, {
        kind: InteractiveElementStatusKind.InProgress,
        partial: null,
      });
    }

    reportLearningProgress(
      enrollmentId,
      id,
      targetKind,
      { correct, total },
      refreshProgress
    );
  }, [
    id,
    attempted,
    finished,
    total,
    correct,
    enrollmentId,
    trainerMode,
    refreshProgress,
  ]);
};
